import { pokemonMasteryXpToNext, pokemonXpToNext } from '../balance-core.mjs';

/** Primeiro marco do endgame do Pokémon; não é um teto de nível. */
export const MASTERY_UNLOCK_LEVEL = 300;

/**
 * Normaliza um nível de Pokémon sem aplicar teto de gameplay.
 * O limite efetivo fica sendo o tipo numérico/inteiro da camada que persiste o dado.
 */
export function normalizePokemonLevel(value = 1) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(1, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(numeric)));
}

function normalizeXp(value = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(Number.MAX_SAFE_INTEGER, Math.floor(numeric)));
}

function normalizeMasteryLevel(value = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(50, Math.floor(numeric)));
}

/**
 * Aplica XP ao Pokémon e, a partir do Nv.300, aplica o mesmo XP à Maestria.
 *
 * A função é deliberadamente independente da UI: o chamador decide como exibir
 * level-ups, atualizar stats e persistir o resultado.
 */
export function applyPokemonXpProgress(
  pokemon = {},
  amount = 0,
) {
  const next = {
    ...pokemon,
    level: normalizePokemonLevel(pokemon.level),
    xp: normalizeXp(pokemon.xp),
    masteryLevel: normalizeMasteryLevel(pokemon.masteryLevel),
    masteryXp: normalizeXp(pokemon.masteryXp),
  };
  const gained = normalizeXp(amount);
  const levels = [];
  const masteryLevels = [];
  let masteryEligibleXp = 0;
  let pending = gained;

  // Resolve o XP novo por partes para que somente o excedente após o marco
  // alimente a Maestria quando um Pokémon cruza o Nv.300 nesta concessão.
  while (pending > 0) {
    if (next.level >= MASTERY_UNLOCK_LEVEL) {
      masteryEligibleXp += pending;
      next.xp += pending;
      pending = 0;
      break;
    }

    const required = normalizeXp(pokemonXpToNext(next.level));
    const remainingForLevel = Math.max(0, required - next.xp);
    if (remainingForLevel === 0) {
      next.xp = 0;
      next.level += 1;
      levels.push(next.level);
      continue;
    }

    const applied = Math.min(pending, remainingForLevel);
    next.xp += applied;
    pending -= applied;
    if (next.xp >= required) {
      next.xp -= required;
      next.level += 1;
      levels.push(next.level);
    }
  }

  // A concessão pode ter deixado XP suficiente para vários níveis acima de 300.
  // Esse loop não tem condição de teto de gameplay.
  while (next.xp >= pokemonXpToNext(next.level)) {
    next.xp -= pokemonXpToNext(next.level);
    next.level += 1;
    levels.push(next.level);
  }

  if (masteryEligibleXp > 0 && next.masteryLevel < 50) {
    next.masteryXp += masteryEligibleXp;
    while (
      next.masteryLevel < 50
      && next.masteryXp >= pokemonMasteryXpToNext(next.masteryLevel)
    ) {
      next.masteryXp -= pokemonMasteryXpToNext(next.masteryLevel);
      next.masteryLevel += 1;
      masteryLevels.push(next.masteryLevel);
    }
    if (next.masteryLevel >= 50) next.masteryXp = 0;
  }

  return {
    pokemon: next,
    gained,
    levels,
    masteryLevels,
    masteryXpGranted: masteryEligibleXp,
  };
}
